import axios from "axios";
import { PATHS } from "../utils/paths";
import { getToken, setToken, clearSessionStorage } from "../utils/session";

const getBaseURL = () => process.env.REACT_APP_URL;

// UX-04 / WEB-04 / SEC-02: single source of truth for tearing down the client
// session. The durable refresh token lives in an HttpOnly cookie now, so there is
// no refresh token to remove here — clearing the cookie is the backend's job on
// /logout. This drops the stored access token + user info.
export const clearSession = () => {
  clearSessionStorage();
};

// UX-04 / WEB-04: global logout — clear the session and hard-redirect to login so
// all in-memory React/store state is reset. Wire this to a logout button.
export const logout = () => {
  // Best-effort backend logout so the HttpOnly refresh cookie is cleared and the
  // refresh token revoked; never block the UI logout on it.
  try {
    const token = getToken();
    instance
      .post(
        "/logout",
        {},
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      )
      .catch(() => {});
  } catch {
    /* ignore */
  }
  clearSession();
  window.location.assign(PATHS.Login);
};

const instance = axios.create({
  baseURL: getBaseURL(),
  // SEC-02: send the HttpOnly refresh cookie on same-site API calls so
  // /refresh_token and /logout can read/rotate/clear it.
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

instance.interceptors.request.use(
  function (config) {
    const token = getToken();

    return token
      ? {
          ...config,
          headers: {
            ...config.headers,
            Authorization: `Bearer ${token}`,
          },
        }
      : config;
  },
  function (error) {
    return Promise.reject(error);
  },
);

// Keep the durable refresh credential in its HttpOnly cookie. The existing
// backend also accepts a Bearer token during its legacy migration period.
// Coordinate rotation across tabs and reuse tokens renewed since a request began.
let refreshPromise = null;

const refreshAccessToken = (failedToken) => {
  if (!refreshPromise) {
    const renew = async () => {
      const currentToken = getToken();
      if (!currentToken) throw new Error("Session has been cleared");
      if (currentToken !== failedToken) return currentToken;
      const res = await axios.post(
        `${getBaseURL()}/refresh_token`,
        {},
        {
          withCredentials: true,
          timeout: 15000,
          headers: { Authorization: `Bearer ${currentToken}` },
        },
      );
      if (getToken() !== currentToken) {
        // A logout or a new login must not be overwritten by a late response.
        throw new Error("Session changed during refresh");
      }
      const token = res.data?.token;
      if (!token) throw new Error("no token in refresh response");
      setToken(token);
      return token;
    };
    const locks = typeof navigator !== "undefined" ? navigator.locks : null;
    const renewal = locks?.request
      ? locks.request(`progpath:refresh:${getBaseURL()}`, renew)
      : renew();
    refreshPromise = renewal.finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

const forceReauth = () => {
  clearSession();
  if (window.location.pathname !== PATHS.Login) {
    window.location.assign(PATHS.Login);
  }
};

instance.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error) {
    const original = error?.config;
    const status = error?.response?.status;
    const isRefreshCall = original?.url?.includes("/refresh_token");
    // A 401 from /login, /login/google, or /register is a credential failure,
    // not an expired session — no refresh attempt, no session teardown; let the
    // form's catch show the real backend error.
    const isCredentialCall = ["/login", "/register", "/logout"].some((p) =>
      original?.url?.includes(p),
    );

    if (status === 401 && isCredentialCall) {
      return Promise.reject(error);
    }

    if (
      status === 401 &&
      original &&
      !original._retried &&
      !isRefreshCall &&
      getToken()
    ) {
      original._retried = true;
      const authorization =
        original.headers?.Authorization ||
        original.headers?.get?.("Authorization");
      const failedToken =
        authorization?.replace(/^Bearer\s+/i, "") || getToken();
      try {
        const token = await refreshAccessToken(failedToken);
        original.headers = {
          ...original.headers,
          Authorization: `Bearer ${token}`,
        };
        return instance(original);
      } catch (refreshError) {
        // Network failures, timeouts, rate limits and server errors are not a
        // rejected session. Preserve it so the next request can retry refresh.
        if (
          refreshError?.response?.status === 401 &&
          getToken() === failedToken
        ) {
          forceReauth();
        }
        return Promise.reject(refreshError);
      }
    }

    // A resource can return 401 even after renewal. It cannot invalidate a
    // successfully refreshed session on its own; propagate that endpoint error.
    return Promise.reject(error);
  },
);

export default instance;
